/* eslint-disable @typescript-eslint/no-explicit-any */
import Utils from '../../../../utils/underscore-es6'
import type { Item, GroupedItem } from '../models'

export enum ProductProperties {
  baseproduct = 'baseproduct',
  colorflavour = 'colorflavour',
  brand = 'brand'
}

export enum GroupByFamilies {
  accessory = 'accessories',
  consumable = 'consumables',
  device = 'devices'
}

type GroupByDataType = {
  productGroups?: Array<{
    productFamily?: string
    productProperty?: string
  }>
  except?: string[]
}

const GroupByProperties = {
  [ProductProperties.baseproduct]: 'baseArticle.baseArticleCode',
  [ProductProperties.colorflavour]: 'nuance.code',
  [ProductProperties.brand]: 'brand'
}

const isNumber = (value?: number) => typeof value === 'number'

const calcGroupingKey = (item: Item, data: GroupByDataType, index: number) => {
  const { productGroups = [], except = [] } = { ...data }
  if (except.includes(item.globalArticleCode)) {
    return {
      groupingKey: index
    }
  }
  const family = Utils.get(item, 'productFamily')
  const familyLowerCase = family.toLowerCase()
  const group = productGroups.find(
    ({ productFamily }) =>
      productFamily ===
      (GroupByFamilies[familyLowerCase as keyof typeof GroupByFamilies] ??
        familyLowerCase)
  )

  if (!group) {
    return {
      groupingKey: index
    }
  }

  const { productFamily, productProperty } = group
  const property =
    GroupByProperties[productProperty as keyof typeof GroupByProperties]

  const key = property ? Utils.get(item, property, property) : index

  return {
    groupingKey: `${productFamily}-${key}`,
    productProperty
  }
}

export const groupBy = (
  items: Item[],
  mapper: (...args: any[]) => any,
  data: GroupByDataType
): { [key: string]: GroupedItem } =>
  items.reduce((acc, item, index) => {
    const { groupingKey, productProperty } = calcGroupingKey(item, data, index)

    // Forced case in baseproduct. If nuance color is different for rings in the future, remove it
    const nuanceUnivocalIndex =
      productProperty === ProductProperties.baseproduct ? index : undefined

    return {
      ...acc,
      [groupingKey]: {
        // ...item,
        ...mapper({ item, acc, key: groupingKey, nuanceUnivocalIndex })
      }
    }
  }, {})

export const productMapper = ({
  item,
  acc,
  key,
  nuanceUnivocalIndex
}: {
  item: Item
  acc: any
  key: string
  nuanceUnivocalIndex?: number
}): GroupedItem => {
  const {
    baseArticle,
    baseArticleCode,
    globalArticleCode: code,
    badges = [],
    nuance = {},
    price = {},
    seo = {},
    tags = [],
    assets = [],
    unitOfMeasure
  } = item
  const prevVariant = [...({ ...acc[key] }?.variants || [])]
  const { code: nuanceCode } = nuance

  return {
    code: baseArticle?.baseArticleCode || baseArticleCode,
    variants: [
      ...prevVariant,
      {
        ...item,
        code,
        badges,
        nuance: {
          ...nuance,
          code: isNumber(nuanceUnivocalIndex)
            ? `${nuanceCode}${nuanceUnivocalIndex}`
            : nuanceCode
        },
        price,
        seo,
        tags,
        assets,
        unitOfMeasure
      }
    ]
  }
}
